import { Component, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'

import { BugReportDialogComponent } from '../bug-report-dialog/bug-report-dialog.component'
import { isReportShortcut, reportShortcutLabel } from '@data/utils/shortcut-label.util'
import { BugReportLauncherService } from '../../data/services/bug-report-launcher.service'
import { TraceService } from '@app/telemetry/trace.service'

//* Tope duro para no dejar la promesa colgada si el rasterizado se atora en una pantalla enorme.
const SCREENSHOT_BUDGET_MS = 8000

//* Lo que NO sale en la foto: el propio diálogo, su máscara y la lengüeta. Sin esto la captura
//* sería una foto del formulario de reporte tapando justo la pantalla que se quiere reportar.
const NOT_IN_SCREENSHOT = ['.bug-tab', '.bug-report-dialog', '.p-dialog-mask', '.p-component-overlay', '.p-toast']

//* Ancho al que se rasteriza. Bajarlo de la resolución real recorta mucho el tiempo y el peso sin
//* que se deje de leer lo que hay en pantalla.
const SCREENSHOT_TARGET_WIDTH = 1400

/**
 * El acceso a «Reportar un problema», disponible desde cualquier pantalla del panel.
 *
 * Es una lengüeta pegada al borde derecho, a media altura, y no un botón redondo en la esquina
 * inferior: esa esquina ya está ocupada en muchas pantallas por el paginador de las tablas y por el
 * pie de los diálogos, donde vive el botón de guardar.
 *
 * **Por qué existe UI nueva** en un panel cuya convención manda poner las acciones globales en el
 * menú del avatar (y ahí también está): la máscara de un diálogo modal cubre el header y lo vuelve
 * inalcanzable, y un fallo dentro de un modal —alta de cliente, comité, previsualizador— es de los
 * casos más frecuentes. Para llegar al menú habría que cerrar el modal, destruyendo justo el estado
 * que se quiere reportar.
 */
@Component({
  selector: 'app-bug-report-launcher',
  templateUrl: './bug-report-launcher.component.html',
  styleUrls: ['./bug-report-launcher.component.scss'],
})
export class BugReportLauncherComponent implements OnInit, OnDestroy {
  private readonly trace = inject(TraceService)
  private readonly launcher = inject(BugReportLauncherService)
  private readonly destroy$ = new Subject<void>()

  @ViewChild(BugReportDialogComponent) dialog?: BugReportDialogComponent

  open = false
  preparing = false

  readonly shortcut = reportShortcutLabel()

  ngOnInit(): void {
    //* El ítem del menú del avatar entra por aquí: ese componente no puede llamar al diálogo
    //* directamente porque vive en otra rama del árbol.
    this.launcher.requests$.pipe(takeUntil(this.destroy$)).subscribe((origin) => void this.launch(origin))
  }

  /** Abre el reporte. `origin` queda en la bitácora para saber por dónde se llegó. */
  async launch(origin: 'tab' | 'menu' | 'shortcut' = 'tab'): Promise<void> {
    if (this.open || this.preparing) return
    this.preparing = true
    this.trace.push('session', `Abrió el reporte de problemas (${origin})`)

    //! El diálogo se abre PRIMERO y la foto se toma después, excluyéndolo.
    //!
    //! El orden inverso parece el natural —fotografiar antes de tapar la pantalla— pero no funciona:
    //! html2canvas ocupa el hilo principal mientras rasteriza, así que el diálogo no podía pintarse
    //! hasta que terminara. Medido contra la pantalla de Clientes, eso dejaba el botón muerto 1.7 s
    //! después del clic. Excluyendo el diálogo de la captura se consigue lo mismo sin la espera.
    this.preparing = false
    this.open = true

    setTimeout(() => {
      this.dialog?.prepare(true)
      //* Un fotograma más para que el diálogo esté pintado antes de empezar a rasterizar; si no, la
      //* foto sale con la pantalla a medio tapar por la máscara que está entrando.
      requestAnimationFrame(() => {
        this.captureScreen().then((file) => this.dialog?.attachAutoShot(file))
      })
    }, 0)
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!isReportShortcut(event)) return
    //! `preventDefault` para que en Mac no se inserte el «®» que produce Option+R.
    event.preventDefault()
    void this.launch('shortcut')
  }

  /**
   * Rasteriza lo que se ve en pantalla.
   *
   * `html2canvas` se carga bajo demanda: este panel no tiene carga diferida por ruta, así que un
   * import normal metería la librería en el paquete que se descarga al entrar al login, para algo
   * que casi nadie usa en una sesión.
   *
   * Se fotografía `document.body` y no el área de contenido, para que salgan el menú, el encabezado
   * y —sobre todo— el diálogo que estuviera abierto. El shell es de posición fija y quien se
   * desplaza es el área de contenido, así que lo visible ya es exactamente lo que se ve.
   */
  private async captureScreen(): Promise<File | null> {
    try {
      return await Promise.race([this.rasterize(), this.timeout(SCREENSHOT_BUDGET_MS)])
    } catch {
      //* Silencio a propósito: no haber podido tomar la foto no es un problema que la persona tenga
      //* que atender. Siempre puede pegar una.
      return null
    }
  }

  private async rasterize(): Promise<File | null> {
    const { default: html2canvas } = await import('html2canvas')

    const canvas = await html2canvas(document.documentElement, {
      backgroundColor: '#ffffff',
      scale: Math.min(1, SCREENSHOT_TARGET_WIDTH / Math.max(window.innerWidth, 1)),
      logging: false,
      useCORS: true,
      imageTimeout: 800,
      removeContainer: true,
      //! Las cuatro dimensiones van explícitas y NO se dejan inferir. El shell del panel es
      //! `position: fixed` y quien se desplaza es el área de contenido, así que el <body> no tiene
      //! alto propio: sin esto html2canvas mide cero y devuelve un lienzo en blanco — que es
      //! exactamente lo que hacía, con la foto saliendo vacía y nadie enterándose.
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      scrollX: 0,
      scrollY: 0,
      //* Fuera de la foto: el diálogo de reporte y su máscara (o la captura sería del propio
      //* formulario), y los iframes, que el rasterizador no puede leer y saldrían en blanco.
      ignoreElements: (el: Element) => el.tagName === 'IFRAME' || NOT_IN_SCREENSHOT.some((sel) => el.matches?.(sel)),
    })

    return new Promise<File | null>((resolve) => {
      //* JPEG y no PNG: una foto de pantalla en PNG pesa unas cuatro veces más sin verse mejor.
      canvas.toBlob((blob) => resolve(blob ? new File([blob], 'pantalla.jpg', { type: 'image/jpeg' }) : null), 'image/jpeg', 0.75)
    })
  }

  private timeout(ms: number): Promise<null> {
    return new Promise((resolve) => setTimeout(() => resolve(null), ms))
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
